import type { GeoEvent, NewsItem, CrisisAlert, AlertLevel, ThreatLevel } from '@/types';

let _alertCounter = 0;
function uniqueAlertId(prefix: string): string {
  return `${prefix}-${Date.now()}-${++_alertCounter}`;
}

// ---------------------------------------------------------------------------
// Crisis Detector — Real-time war-risk / crisis alert engine
// Analyzes incoming GeoEvents and NewsItems for crisis signals
// ---------------------------------------------------------------------------

// High-signal keywords indicating imminent conflict
const EXTREME_KEYWORDS = [
  'nuclear', 'nuke', 'icbm', 'ballistic missile', 'nuclear warhead', 'nuclear strike',
  'declaration of war', 'martial law', 'mass mobilization', 'general mobilization',
  'chemical weapon', 'biological weapon', 'dirty bomb',
  'carrier group', 'naval blockade', 'amphibious assault',
  'red alert', 'level 5 alert', 'maximum alert',
];

const CRITICAL_KEYWORDS = [
  'invasion', 'invade', 'missile strike', 'artillery barrage', 'air raid',
  'military offensive', 'ground offensive', 'troops deployed', 'war declared',
  'coup', 'overthrow', 'regime change', 'emergency decree',
  'drone attack', 'suicide drone', 'kamikaze drone',
  'terror attack', 'explosion', 'bombing campaign',
  'sanctions escalation', 'oil embargo', 'asset freeze',
];

const WARNING_KEYWORDS = [
  'military exercise', 'war game', 'live fire drill', 'troops massing',
  'border closure', 'evacuation', 'no-fly zone', 'flight ban',
  'ceasefire collapsed', 'ceasefire violated', 'truce broken',
  'hostage', 'kidnapping', 'assassination', 'targeted killing',
  'protest', 'riot', 'unrest', 'clashes', 'demonstration',
  'diplomatic expulsion', 'ambassador recall', 'breaking off relations',
  'sanctions', 'tariffs', 'trade war',
];

// Regional keyword to coordinate mapping
const REGION_COORDS: Record<string, { lat: number; lon: number; name: string }> = {
  ukraine: { lat: 49.0, lon: 32.0, name: 'Ukraine' },
  russia: { lat: 55.0, lon: 37.0, name: 'Russia' },
  gaza: { lat: 31.4, lon: 34.4, name: 'Gaza' },
  israel: { lat: 31.8, lon: 35.2, name: 'Israel' },
  taiwan: { lat: 23.7, lon: 121.0, name: 'Taiwan' },
  china: { lat: 35.0, lon: 105.0, name: 'China' },
  'north korea': { lat: 40.0, lon: 127.0, name: 'North Korea' },
  korea: { lat: 37.0, lon: 127.5, name: 'Korean Peninsula' },
  iran: { lat: 32.0, lon: 53.0, name: 'Iran' },
  syria: { lat: 35.0, lon: 38.5, name: 'Syria' },
  iraq: { lat: 33.0, lon: 44.0, name: 'Iraq' },
  pakistan: { lat: 30.0, lon: 69.0, name: 'Pakistan' },
  india: { lat: 20.0, lon: 78.0, name: 'India' },
  sudan: { lat: 15.5, lon: 32.5, name: 'Sudan' },
  myanmar: { lat: 20.0, lon: 96.0, name: 'Myanmar' },
  yemen: { lat: 15.5, lon: 47.0, name: 'Yemen' },
  'south china sea': { lat: 13.0, lon: 114.0, name: 'South China Sea' },
  nato: { lat: 50.8, lon: 4.3, name: 'NATO' },
  europe: { lat: 50.0, lon: 15.0, name: 'Europe' },
  'red sea': { lat: 20.0, lon: 38.0, name: 'Red Sea' },
  gulf: { lat: 26.0, lon: 51.0, name: 'Persian Gulf' },
  africa: { lat: 8.0, lon: 20.0, name: 'Africa' },
};

interface KeywordHit {
  keyword: string;
  level: AlertLevel;
  count: number;
}

function scoreText(text: string): { level: AlertLevel | null; hits: KeywordHit[] } {
  const lower = text.toLowerCase();
  const hits: KeywordHit[] = [];

  for (const kw of EXTREME_KEYWORDS) {
    const count = (lower.match(new RegExp(kw, 'g')) ?? []).length;
    if (count > 0) hits.push({ keyword: kw, level: 'extreme', count });
  }
  for (const kw of CRITICAL_KEYWORDS) {
    const count = (lower.match(new RegExp(kw, 'g')) ?? []).length;
    if (count > 0) hits.push({ keyword: kw, level: 'critical', count });
  }
  for (const kw of WARNING_KEYWORDS) {
    const count = (lower.match(new RegExp(kw, 'g')) ?? []).length;
    if (count > 0) hits.push({ keyword: kw, level: 'warning', count });
  }

  if (!hits.length) return { level: null, hits: [] };

  if (hits.some((h) => h.level === 'extreme')) return { level: 'extreme', hits };
  if (hits.some((h) => h.level === 'critical')) return { level: 'critical', hits };
  return { level: 'warning', hits };
}

function detectRegion(text: string): string {
  const lower = text.toLowerCase();
  for (const [key, val] of Object.entries(REGION_COORDS)) {
    if (lower.includes(key)) return val.name;
  }
  return 'Global';
}

function getRegionCoords(regionName: string): { lat: number; lon: number } | undefined {
  const entry = Object.values(REGION_COORDS).find((v) => v.name === regionName);
  return entry ? { lat: entry.lat, lon: entry.lon } : undefined;
}

function alertLevelFromSeverity(s: ThreatLevel): AlertLevel | null {
  if (s === 'critical') return 'extreme';
  if (s === 'high') return 'critical';
  if (s === 'medium') return 'warning';
  return null;
}

// ---------------------------------------------------------------------------
// Main detection function
// ---------------------------------------------------------------------------

export interface CrisisDetectionResult {
  alerts: CrisisAlert[];
  riskScore: number; // 0-100 overall session risk score
}

export function detectCrises(
  events: GeoEvent[],
  news: NewsItem[],
  existingAlerts: CrisisAlert[],
): CrisisDetectionResult {
  const now = new Date().toISOString();
  const newAlerts: CrisisAlert[] = [];
  const seenRegions = new Set<string>();

  // Analyze high-severity events
  const criticalEvents = events.filter(
    (e) => e.severity === 'critical' || e.severity === 'high'
  );

  // Score news items
  interface ScoredNews {
    item: NewsItem;
    level: AlertLevel;
    hits: KeywordHit[];
    region: string;
  }

  const scoredNews: ScoredNews[] = [];
  for (const item of news.slice(0, 50)) {
    const combined = `${item.title} ${item.description ?? ''}`;
    const { level, hits } = scoreText(combined);
    if (level && hits.length >= 2) {
      scoredNews.push({
        item,
        level,
        hits,
        region: detectRegion(combined),
      });
    }
  }

  // Group by region for deduplication
  const regionGroups = new Map<string, ScoredNews[]>();
  for (const sn of scoredNews) {
    const key = sn.region;
    if (!regionGroups.has(key)) regionGroups.set(key, []);
    regionGroups.get(key)!.push(sn);
  }

  // Create alerts from regional clusters
  for (const [region, items] of regionGroups) {
    if (seenRegions.has(region)) continue;

    const extremeCount = items.filter((i) => i.level === 'extreme').length;
    const criticalCount = items.filter((i) => i.level === 'critical').length;

    let level: AlertLevel;
    if (extremeCount >= 1) level = 'extreme';
    else if (criticalCount >= 2) level = 'critical';
    else if (items.length >= 3) level = 'critical';
    else level = 'warning';

    // Check if identical alert already exists and is not dismissed
    const existing = existingAlerts.find(
      (a) => a.region === region && a.level === level && !a.dismissed
    );
    if (existing) continue;

    const topItem = items[0];
    const allKeywords = items.flatMap((i) => i.hits.map((h) => h.keyword));
    const uniqueKeywords = [...new Set(allKeywords)].slice(0, 5);

    const confidence = Math.min(
      100,
      items.length * 15 + extremeCount * 25 + criticalCount * 10
    );

    const alertTitleEn = buildAlertTitle(region, level, uniqueKeywords);
    const alertTitleKo = buildAlertTitleKo(region, level, uniqueKeywords);

    const regionCoords = getRegionCoords(region);
    newAlerts.push({
      id: uniqueAlertId(`alert-${region.replace(/\s/g, '-').toLowerCase()}`),
      level,
      title: alertTitleEn,
      titleKo: alertTitleKo,
      region,
      ...(regionCoords ?? {}),
      summary: topItem.item.title,
      confidence,
      sources: [...new Set(items.map((i) => i.item.source))].slice(0, 4),
      timestamp: now,
      relatedEventIds: criticalEvents.slice(0, 5).map((e) => e.id),
      dismissed: false,
    });

    seenRegions.add(region);
    if (newAlerts.length >= 5) break; // Max 5 new alerts per cycle
  }

  // Also generate alerts from high-severity event clusters
  const eventAlerts = generateEventClusterAlerts(criticalEvents, existingAlerts, now);
  for (const ea of eventAlerts) {
    if (!seenRegions.has(ea.region)) {
      newAlerts.push(ea);
      seenRegions.add(ea.region);
      if (newAlerts.length >= 8) break;
    }
  }

  // Merge with non-dismissed existing alerts
  const allAlerts = [
    ...existingAlerts.filter((a) => !a.dismissed),
    ...newAlerts,
  ].slice(0, 10); // Cap at 10 active alerts

  // Calculate overall risk score
  const riskScore = calculateRiskScore(allAlerts, criticalEvents);

  return { alerts: allAlerts, riskScore };
}

function generateEventClusterAlerts(
  events: GeoEvent[],
  existing: CrisisAlert[],
  now: string,
): CrisisAlert[] {
  const alerts: CrisisAlert[] = [];

  // Group by country
  const byCountry = new Map<string, GeoEvent[]>();
  for (const e of events) {
    const key = e.country ?? e.countryCode ?? 'Unknown';
    if (!byCountry.has(key)) byCountry.set(key, []);
    byCountry.get(key)!.push(e);
  }

  for (const [country, evts] of byCountry) {
    if (evts.length < 3) continue;

    const hasCritical = evts.some((e) => e.severity === 'critical');
    const level: AlertLevel = hasCritical ? 'critical' : 'warning';

    const existsAlready = existing.some(
      (a) => a.region === country && a.level === level && !a.dismissed
    );
    if (existsAlready) continue;

    // Use centroid of events as coordinates, fallback to REGION_COORDS
    const avgLat = evts.reduce((s, e) => s + e.lat, 0) / evts.length;
    const avgLon = evts.reduce((s, e) => s + e.lon, 0) / evts.length;
    const clusterCoords = getRegionCoords(country) ?? { lat: avgLat, lon: avgLon };
    alerts.push({
      id: uniqueAlertId(`alert-cluster-${country.replace(/\s/g, '-').toLowerCase()}`),
      level,
      title: `Elevated Activity Cluster: ${country}`,
      titleKo: `활동 급증 감지: ${country}`,
      region: country,
      ...clusterCoords,
      summary: `${evts.length} events detected in ${country} region within monitoring window`,
      confidence: Math.min(100, evts.length * 20),
      sources: [...new Set(evts.map((e) => e.source))].slice(0, 3),
      timestamp: now,
      relatedEventIds: evts.slice(0, 5).map((e) => e.id),
      dismissed: false,
    });
  }

  return alerts;
}

function buildAlertTitle(region: string, level: AlertLevel, keywords: string[]): string {
  const topKw = keywords[0] ?? 'conflict';
  if (level === 'extreme') return `⚠ EXTREME ALERT: ${region} — ${topKw.toUpperCase()} activity detected`;
  if (level === 'critical') return `🔴 CRITICAL: Military escalation detected in ${region}`;
  return `🟡 WARNING: Elevated threat activity — ${region}`;
}

function buildAlertTitleKo(region: string, level: AlertLevel, keywords: string[]): string {
  if (level === 'extreme') return `⚠ 극도 경보: ${region} — 핵/대량파괴무기 관련 활동 감지`;
  if (level === 'critical') return `🔴 위급 경보: ${region} 지역 군사 충돌 고조`;
  return `🟡 경고: ${region} 지역 위협 활동 감지`;
}

function calculateRiskScore(alerts: CrisisAlert[], criticalEvents: GeoEvent[]): number {
  let score = 0;
  for (const a of alerts) {
    if (a.dismissed) continue;
    if (a.level === 'extreme') score += 25;
    else if (a.level === 'critical') score += 15;
    else score += 5;
  }
  score += Math.min(30, criticalEvents.length * 2);
  return Math.min(100, score);
}
